import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Trophy, ArrowLeft, Trash } from '@phosphor-icons/react';
import SEO from '../components/SEO';
import {
  TournamentBasicsSection,
  TournamentLocationSection,
  TournamentScheduleSection,
  TournamentMediaSection,
  TournamentContentSection,
  TournamentPaymentSection
} from '../components/tournament/TournamentFormSections';
import { logError } from '../lib/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EditTournament = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDel, setShowDel] = useState(false);
  const [f, setF] = useState(null);

  useEffect(() => {
    axios.get(`${API}/tournaments/${id}`).then(({ data }) => {
      setF({
        name: data.name || '', format: data.format || '5v5', city: data.city || '', state: data.state || '',
        venue: data.venue || '',
        registration_opens_at: data.registration_opens_at || '',
        registration_closes_at: data.registration_closes_at || '',
        starts_at: data.starts_at || '',
        ends_at: data.ends_at || '',
        // Preserve legacy free-text dates for records that don't have structured fields yet.
        dates: data.dates || '',
        registration_deadline: data.registration_deadline || '',
        age_categories: data.age_categories || [],
        gender_categories: data.gender_categories || [],
        age_category: data.age_category || '',
        gender_category: data.gender_category || '',
        rules: data.rules || '', prizes: data.prizes || '', entry_fee: data.entry_fee || '',
        registration_fee: data.registration_fee ?? 0,
        fee_mode: data.fee_mode || 'single',
        category_fees: data.category_fees || null,
        payment_method: data.payment_method || 'free',
        upi_id: data.upi_id || '',
        upi_qr_url: data.upi_qr_url || '',
        payment_instructions: data.payment_instructions || '',
        banner_url: data.banner_url || '',
        banner_focus_x: (data.banner_focus_x ?? 50),
        banner_focus_y: (data.banner_focus_y ?? 50),
        description: data.description || '', google_maps_link: data.google_maps_link || ''
      });
    }).catch((e) => {
      logError('Failed to load tournament:', e);
      setError('Failed to load tournament');
    });
  }, [id]);

  const onChange = (e) => setF({ ...f, [e.target.name]: e.target.value });
  const onBannerChange = (url) => setF({ ...f, banner_url: url });
  const onFocusChange = (fx, fy) => setF((prev) => ({ ...prev, banner_focus_x: fx, banner_focus_y: fy }));

  const submit = async (e) => {
    e.preventDefault();
    if ((f.age_categories || []).length === 0) { setError('Select at least one age category'); return; }
    if ((f.gender_categories || []).length === 0) { setError('Select at least one gender category'); return; }
    setError(''); setLoading(true);
    try {
      await axios.put(`${API}/tournaments/${id}`, f, { withCredentials: true });
      navigate('/organizer');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update');
    } finally {
      setLoading(false);
    }
  };

  const del = async () => {
    try {
      await axios.post(`${API}/tournaments/${id}/request-deletion`, {}, { withCredentials: true });
      setShowDel(false);
      window.alert('Deletion request submitted. An administrator will review it shortly.');
      navigate('/organizer');
    } catch (err) {
      logError('Delete request failed:', err);
      window.alert(err.response?.data?.detail || 'Failed to submit deletion request');
    }
  };

  if (!f) return <div className="min-h-screen pt-24 flex items-center justify-center text-gray-500">Loading...</div>;

  return (
    <>
      <SEO title="Edit Tournament" description="Edit your tournament details." path="/organizer/tournaments/:id/edit" />
      <div className="pt-24 pb-16 min-h-screen">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate('/organizer')} className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#F97316] mb-6" data-testid="back-link">
            <ArrowLeft size={16} weight="bold" /> Back to dashboard
          </button>

          <div className="flex justify-between items-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900" data-testid="edit-tournament-title">Edit tournament</h1>
            <button onClick={() => setShowDel(true)} className="inline-flex items-center gap-1.5 bg-red-50 hover:bg-red-100 text-red-700 px-3 py-2 rounded-lg text-xs font-semibold" data-testid="request-deletion-btn">
              <Trash size={14} weight="bold" /> Request Deletion
            </button>
          </div>

          {error && <div className="bg-red-50 border border-red-100 text-red-700 px-4 py-3 mb-5 text-sm rounded-xl">{error}</div>}

          <form onSubmit={submit} className="bg-white border border-gray-200 rounded-2xl p-6 space-y-5">
            <TournamentBasicsSection f={f} onChange={onChange} setF={setF} />
            <TournamentLocationSection f={f} onChange={onChange} />
            <TournamentScheduleSection f={f} setF={setF} />
            <TournamentPaymentSection f={f} onChange={onChange} setF={setF} />
            <TournamentMediaSection f={f} onChange={onChange} onBannerChange={onBannerChange} onFocusChange={onFocusChange} />
            <TournamentContentSection f={f} onChange={onChange} />

            <button type="submit" disabled={loading} className="btn-primary w-full px-5 py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="save-tournament-btn">
              {loading ? 'Saving...' : 'Save changes'} {!loading && <Trophy size={16} weight="bold" />}
            </button>
          </form>

          {showDel && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl p-7 max-w-md w-full shadow-2xl">
                <h2 className="text-xl font-bold text-gray-900 mb-2">Request tournament deletion?</h2>
                <p className="text-sm text-gray-600 mb-5">Are you sure you want to request deletion of this tournament? An administrator will review your request. Historical registrations will be preserved for audit.</p>
                <div className="flex gap-2">
                  <button onClick={() => setShowDel(false)} className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-900 px-4 py-2.5 rounded-xl text-sm font-semibold">Cancel</button>
                  <button onClick={del} className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold" data-testid="confirm-request-deletion-btn">Request Deletion</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EditTournament;
